import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_OPTIONS, SupportedLanguage, SUPPORTED_LANGUAGES } from '../../i18n/utils/language';

interface LanguageSelectorProps {
  onLanguageChange?: (language: string) => void;
}

export default function LanguageSelector({ onLanguageChange }: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleLanguageChange = (languageCode: string) => {
    // Only allow supported languages to actually change
    if (SUPPORTED_LANGUAGES.includes(languageCode as SupportedLanguage)) {
      i18n.changeLanguage(languageCode as SupportedLanguage);
    }

    setShowModal(false);
    onLanguageChange?.(languageCode);
  };

  return (
    <>
      {/* Language trigger button */}
      <button
        onClick={() => setShowModal(true)}
        className="text-blue-500 hover:text-blue-600 transition-colors cursor-pointer flex items-center space-x-1"
      >
        <span>{t('footer.moreLanguage')}</span>
        <i className="ri-arrow-right-line"></i>
        <i className="ri-arrow-right-line"></i>
      </button>

      {/* Language Selection Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden border border-gray-200">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">{t('label.selectLanguage')}</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-xl"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {LANGUAGE_OPTIONS.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    className={`flex items-center space-x-3 p-4 rounded-2xl transition-all duration-300 text-left border-2 transform hover:scale-105 ${
                      i18n.language === language.code
                        ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-300 shadow-md'
                        : SUPPORTED_LANGUAGES.includes(language.code as SupportedLanguage)
                          ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 border-gray-200 hover:border-blue-300 hover:shadow-md'
                          : 'border-gray-200 opacity-50 cursor-not-allowed hover:scale-100'
                    }`}
                    disabled={!SUPPORTED_LANGUAGES.includes(language.code as SupportedLanguage)}
                  >
                    <span className="text-xl">{language.flag}</span>
                    <span className={`text-sm font-medium truncate ${
                      SUPPORTED_LANGUAGES.includes(language.code as SupportedLanguage) ? 'text-gray-800' : 'text-gray-400'
                    }`}>
                      {language.nativeName}
                    </span>
                    {!SUPPORTED_LANGUAGES.includes(language.code as SupportedLanguage) && (
                      <span className="text-xs text-gray-400 ml-auto">Soon</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-gray-600">
                  <i className="ri-information-line mr-2"></i>
                  Currently supporting {SUPPORTED_LANGUAGES.length} languages. Select your preferred language from the options above.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}